"use client";

import { useState, useEffect, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/hooks/use-auth";
import { TIERS } from "@workspace/tier-config";
import { ArrowRight } from "lucide-react";

const TIER_MAP = Object.fromEntries(TIERS.map((t) => [t.key, t]));

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function WebflowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M192.273 0H63.727C28.528 0 0 28.528 0 63.727v128.546C0 227.472 28.528 256 63.727 256h128.546C227.472 256 256 227.472 256 192.273V63.727C256 28.528 227.472 0 192.273 0Z" fill="#146EF5" />
      <path d="M173.426 80s-22.03 69.516-23.098 73.017c-.427-2.989-13.397-73.017-13.397-73.017h-26.386s-13.824 70.666-14.25 73.351C95.226 150.37 72.574 80 72.574 80H46l33.63 96h27.744l13.866-66.835L135.106 176h27.744L196.48 80h-23.054Z" fill="#fff" />
    </svg>
  );
}

type AuthState = "idle" | "sending" | "redirecting";

function FunnelStepper({ planChosen }: { planChosen: boolean }) {
  const steps = planChosen
    ? [
        { label: "Analyze", done: true, active: false },
        { label: "Plan", done: true, active: false },
        { label: "Sign up", done: false, active: true },
        { label: "Dashboard", done: false, active: false },
      ]
    : [
        { label: "Analyze", done: true, active: false },
        { label: "Plan", done: false, active: false },
        { label: "Sign up", done: false, active: true },
        { label: "Dashboard", done: false, active: false },
      ];

  return (
    <div className="flex items-center gap-0 mb-10">
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
              s.done
                ? "bg-blue-600 border-blue-600 text-white"
                : s.active
                ? "border-blue-500 text-blue-400 bg-transparent"
                : "border-white/15 text-white/25 bg-transparent"
            }`}>
              {s.done ? "✓" : i + 1}
            </div>
            <span className={`text-xs mt-1 ${s.active ? "text-white" : s.done ? "text-blue-400" : "text-white/30"}`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`w-12 h-px mb-4 mx-1 ${s.done ? "bg-blue-600" : "bg-white/10"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function SignupContent() {
  const router = useRouter();
  const params = useSearchParams();
  const { user, loading } = useAuth({ skipRedirect: true });

  const tierParam = params?.get("tier") ?? "";
  const redirectParam = params?.get("redirect") ?? "";
  const urlParam = params?.get("url") ?? "";
  const cmsParam = params?.get("cms") ?? "";
  const pathParam = params?.get("path") ?? "";
  const providerParam = params?.get("provider") ?? "";
  const pagesParam = params?.get("pages") ?? "";

  const fromPreview = !!urlParam;
  const planChosen = !!tierParam;

  const setupDomain = (() => {
    if (!urlParam) return null;
    try {
      return new URL(urlParam).hostname;
    } catch { return urlParam.replace(/^https?:\/\//, "").split("/")[0]; }
  })();

  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const [email, setEmail] = useState("");
  const [authState, setAuthState] = useState<AuthState>("idle");
  const [error, setError] = useState<string | null>(null);

  const chosenTier = tierParam ? TIER_MAP[tierParam] : null;
  const isFree = chosenTier?.price.monthly === 0;

  function buildDestination() {
    if (redirectParam && redirectParam.startsWith("/")) return redirectParam;

    if (tierParam && fromPreview) {
      if (isFree) return "/dashboard";
      const p = new URLSearchParams();
      p.set("chosen", tierParam);
      if (urlParam) p.set("url", urlParam);
      if (cmsParam) p.set("cms", cmsParam);
      if (pathParam) p.set("path", pathParam);
      if (pagesParam) p.set("pages", pagesParam);
      if (providerParam) p.set("provider", providerParam);
      return `/onboarding/plan?${p.toString()}`;
    }

    if (tierParam && !fromPreview) {
      if (isFree) return "/dashboard";
      return `/checkout?tier=${tierParam}&fromOnboarding=true`;
    }

    return "/dashboard";
  }

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && user) {
      router.replace(buildDestination());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  // Redirect to plan selection if missing tier
  useEffect(() => {
    if (!loading && !user && fromPreview && !tierParam) {
      const p = new URLSearchParams();
      if (urlParam) p.set("url", urlParam);
      if (cmsParam) p.set("cms", cmsParam);
      if (pathParam) p.set("path", pathParam);
      if (pagesParam) p.set("pages", pagesParam);
      if (providerParam) p.set("provider", providerParam);
      router.replace(`/onboarding/plan?${p.toString()}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, fromPreview, tierParam]);

  async function handleEmailSignup(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setError(null);
    setAuthState("sending");

    try {
      const destination = buildDestination();
      const res = await fetch("/api/auth/magic-link", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), redirect: destination }),
      });

      const body = await res.json() as { ok?: boolean; redirect?: string; error?: string };

      if (!res.ok) {
        throw new Error(body.error ?? "Failed to sign up. Please try again.");
      }

      if (body.ok && body.redirect) {
        setAuthState("redirecting");
        router.replace(body.redirect);
        return;
      }

      // Fallback — shouldn't happen with new API
      setAuthState("redirecting");
      router.replace(destination);
    } catch (err) {
      setError((err as Error).message);
      setAuthState("idle");
    }
  }

  // Build Google OAuth URL with funnel context
  function buildGoogleHref() {
    const p = new URLSearchParams();
    if (tierParam) p.set("tier", tierParam);
    if (urlParam) p.set("url", urlParam);
    if (cmsParam) p.set("cms", cmsParam);
    if (pathParam) p.set("path", pathParam);
    if (pagesParam) p.set("pages", pagesParam);
    if (providerParam) p.set("provider", providerParam);
    if (!tierParam || !urlParam) {
      const dest = buildDestination();
      if (dest !== "/dashboard") p.set("redirect", dest);
    }
    const qs = p.toString();
    return `/api/auth/google${qs ? `?${qs}` : ""}`;
  }

  const webflowHref = `/api/auth/webflow?redirect=${encodeURIComponent(buildDestination())}`;

  if (!mounted || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (authState === "redirecting") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0F172A] px-4 py-12">
      {fromPreview && <FunnelStepper planChosen={planChosen} />}

      <div className="w-full max-w-[440px]">
        {!fromPreview && (
          <div className="flex justify-center mb-10">
            <Link href="/">
              <picture>
                <source srcSet="/images/legible-logo.webp" type="image/webp" />
                <img src="/images/legible-logo.png" alt="Legible" className="h-8 w-auto invert" width={110} height={32} />
              </picture>
            </Link>
          </div>
        )}

        <div className="rounded-2xl border border-white/10 bg-[#1E293B] px-8 py-10">
          <h1 className="text-2xl font-bold text-white tracking-tight mb-2 text-center">
            Create your account
          </h1>

          {chosenTier && setupDomain && (
            <div className="mb-6 text-center">
              <p className="text-sm text-white/50 mb-2">
                Setting up <span className="text-white/80 font-medium">{setupDomain}</span>
              </p>
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-blue-500/10 text-blue-300 text-xs font-semibold border border-blue-500/20">
                ✦ {chosenTier.name} plan
                {chosenTier.price.monthly != null && chosenTier.price.monthly > 0
                  ? ` · $${chosenTier.price.monthly}/mo`
                  : " · Free forever"}
              </span>
            </div>
          )}

          {chosenTier && !setupDomain && (
            <div className="mb-6 flex items-center justify-center">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-blue-500/10 text-blue-300 text-xs font-semibold border border-blue-500/20">
                ✦ {chosenTier.name} plan
                {chosenTier.price.monthly != null && chosenTier.price.monthly > 0
                  ? ` · $${chosenTier.price.monthly}/mo`
                  : " · Free forever"}
              </span>
            </div>
          )}

          {!chosenTier && setupDomain && (
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="text-sm text-white/50">Setting up:</span>
              <span className="text-sm font-semibold text-white/90">{setupDomain}</span>
            </div>
          )}

          {!chosenTier && !setupDomain && (
            <p className="text-sm text-white/50 text-center mb-6">
              Make your content readable by every AI on the internet.
            </p>
          )}

          {error && (
            <div className="mb-5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-300">
              {error}
            </div>
          )}

          {/* ── Google sign-in (primary) ── */}
          <a
            href={buildGoogleHref()}
            className="flex items-center justify-center gap-2.5 w-full px-5 py-3.5 rounded-xl bg-white text-gray-800 font-semibold text-sm hover:bg-gray-50 border border-gray-200 transition-colors mb-4 shadow-sm"
          >
            <GoogleIcon />
            Continue with Google
          </a>

          {/* Show Webflow only for Webflow CMS or non-funnel */}
          {(!fromPreview || cmsParam === "webflow" || cmsParam === "Webflow") && (
            <a
              href={webflowHref}
              className="flex items-center justify-center gap-2.5 w-full px-5 py-3.5 rounded-xl bg-[#146EF5] text-white font-semibold text-sm hover:bg-[#0f5fd8] transition-colors mb-4"
            >
              <WebflowIcon />
              Continue with Webflow
            </a>
          )}

          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#1E293B] px-3 text-xs text-white/30">or continue with email</span>
            </div>
          </div>

          {/* ── Email sign-up (secondary, non-blocking) ── */}
          <form onSubmit={(e) => { void handleEmailSignup(e); }} className="space-y-3">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/70 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white text-sm placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={authState === "sending"}
              className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-500 transition-colors disabled:opacity-60"
            >
              {authState === "sending"
                ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Signing up…</>
                : <>Continue with email <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-5 text-xs text-white/30 text-center">
            {chosenTier && !isFree
              ? `${chosenTier.trialDays}-day free trial · Cancel anytime`
              : "Free plan available · No commitment"}
          </p>

          <p className="mt-4 text-xs text-white/25 text-center">
            By continuing, you agree to our{" "}
            <Link href="/terms/" className="underline underline-offset-2 hover:text-white/50">Terms</Link>
            {" "}and{" "}
            <Link href="/privacy/" className="underline underline-offset-2 hover:text-white/50">Privacy Policy</Link>.
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-white/40">
          Already have an account?{" "}
          <Link href="/login/" className="text-blue-400 font-medium hover:text-blue-300">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <SignupContent />
    </Suspense>
  );
}
